import { autoResize } from './context';
import { getMsgInput } from './dom';

type AskUserOption = {
  label?: string;
  description?: string;
};

type AskUserQuestion = {
  header?: string;
  question?: string;
  options?: AskUserOption[];
};

function normalizeAskUserInput(input: unknown): { questions?: unknown } | null {
  if (input === null || input === undefined) return null;
  if (typeof input === 'string') {
    const trimmed = input.trim();
    if (!trimmed) return null;
    try {
      return JSON.parse(trimmed);
    } catch {
      return null;
    }
  }
  return typeof input === 'object' ? input as { questions?: unknown } : null;
}

export function extractAskUserQuestions(input: unknown): AskUserQuestion[] {
  const parsed = normalizeAskUserInput(input);
  if (!parsed || !Array.isArray(parsed.questions)) return [];
  return parsed.questions as AskUserQuestion[];
}

function appendAskOptionToInput(question: AskUserQuestion, option: AskUserOption): void {
  const msgInput = getMsgInput();
  if (!msgInput) return;
  const header = (question?.header || '').trim() || '问题';
  const line = `【${header}】${option?.label || ''}`;
  const current = msgInput.value.trim();
  msgInput.value = current ? `${current}\n${line}` : line;
  autoResize();
  msgInput.focus();
}

export function createAskUserQuestionView(questions: AskUserQuestion[]): HTMLDivElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'ask-user-question';

  questions.forEach((q, idx) => {
    const card = document.createElement('div');
    card.className = 'ask-question-card';

    const header = document.createElement('div');
    header.className = 'ask-question-header';
    header.textContent = `${idx + 1}. ${q.header || '问题'}`;
    card.appendChild(header);

    const body = document.createElement('div');
    body.className = 'ask-question-text';
    body.textContent = q.question || '';
    card.appendChild(body);

    if (Array.isArray(q.options) && q.options.length > 0) {
      const hasDesc = q.options.some((o) => o.description);
      const layout = document.createElement('div');
      layout.className = `ask-options-layout${hasDesc ? ' has-preview' : ''}`;
      const opts = document.createElement('div');
      opts.className = 'ask-question-options';
      const preview = hasDesc ? document.createElement('div') : null;
      if (preview) {
        preview.className = 'ask-option-preview';
        preview.textContent = q.options[0].description || '';
      }
      let selectedOpt: AskUserOption | null = null;
      let selectedBtn: HTMLButtonElement | null = null;

      q.options.forEach((opt, i) => {
        const item = document.createElement('button');
        item.type = 'button';
        item.className = 'ask-option-item';
        const title = document.createElement('div');
        title.className = 'ask-option-label';
        title.textContent = `${i + 1}. ${opt.label || ''}`;
        item.appendChild(title);
        if (preview) {
          item.addEventListener('mouseenter', () => {
            preview.textContent = opt.description || '';
          });
        }
        item.addEventListener('click', () => {
          const isTouch = item.dataset.touchActivated === '1';
          item.dataset.touchActivated = '';
          if (isTouch) {
            if (selectedBtn !== item) {
              if (selectedBtn) selectedBtn.classList.remove('ask-option-selected');
              selectedBtn = item;
              selectedOpt = opt;
              item.classList.add('ask-option-selected');
              if (preview) preview.textContent = opt.description || '';
              return;
            }
          }
          appendAskOptionToInput(q, opt);
        });
        item.addEventListener('touchstart', () => {
          item.dataset.touchActivated = '1';
        }, { passive: true });
        opts.appendChild(item);
      });

      layout.appendChild(opts);
      if (preview) {
        layout.appendChild(preview);
        requestAnimationFrame(() => {
          preview.style.minHeight = `${opts.offsetHeight}px`;
        });
      }
      if (hasDesc) {
        const confirmBtn = document.createElement('button');
        confirmBtn.type = 'button';
        confirmBtn.className = 'ask-confirm-btn';
        confirmBtn.textContent = '确认选择';
        confirmBtn.addEventListener('click', () => {
          if (selectedOpt) {
            appendAskOptionToInput(q, selectedOpt);
          } else if (q.options && q.options.length > 0) {
            appendAskOptionToInput(q, q.options[0]);
          }
        });
        layout.appendChild(confirmBtn);
      }
      card.appendChild(layout);
    }
    wrapper.appendChild(card);
  });
  return wrapper;
}
